'use client'

import { useCallback, useMemo, useState } from 'react'
import type { AncestorStep } from '@/lib/rendering/email-jsx-tiptap/element-context'
import { newId } from '@/lib/shared/ids'

/**
 * Per-element-edit lifecycle. Edits go straight from `start()` into
 * `processing` — no queued / dispatching state, because the new async
 * endpoint (`POST /api/element-edit`) is fired immediately and runs
 * IN PARALLEL with any other in-flight edits on the same email.
 *
 *   start() → processing → complete() (entry removed)
 *
 * This module lives under `components/global-chat/` because the
 * tracker is owned by the layout-level `GlobalChatProvider` so both
 * the global chat composer (compose lock) and the emails canvas (toolbar,
 * editor placeholder, and live-card residency) read the same in-flight set. The
 * legacy path
 * `app/(app)/(workspace)/emails/(canvas)/use-element-edit-queue.ts`
 * re-exports from here for back-compat.
 */
export type PendingEditStatus = 'processing'

export interface PendingEdit {
  /** Stable client-generated id shared by the plugin + completion match. */
  id: string
  emailId: string
  prompt: string
  nodeType: string
  jsxSnippet: string
  ancestorsPath: Array<AncestorStep>
  /** Initial ProseMirror target position; the editor plugin maps it live. */
  pos: number
  /** ms since epoch when `start()` fired. Retained for telemetry / debug logs only. */
  submitTs: number
  status: PendingEditStatus
}

export interface StartElementEditInput {
  emailId: string
  prompt: string
  nodeType: string
  jsxSnippet: string
  ancestorsPath: Array<AncestorStep>
  pos: number
}

/**
 * Single source of truth for "this email is in-flight" predicates. Both
 * the compose-lock filter (which removes an email from chat selection)
 * and the toolbar pill count derive from this set, so they can never
 * disagree about whether the email is locked.
 */
const IN_FLIGHT_STATUSES: ReadonlySet<PendingEditStatus> = new Set([
  'processing',
])

export interface UseElementEditTrackerResult {
  /**
   * Begin tracking a new in-flight element edit. Returns the id the
   * caller uses to remove the entry after success or failure. The caller is
   * responsible for actually firing the request — this hook only tracks UI
   * state.
   */
  start: (input: StartElementEditInput) => string
  /** Success or failure cleanup — removes the processing entry. */
  complete: (id: string) => void
  /** All edits for an email (any status). */
  listForEmail: (emailId: string) => Array<PendingEdit>
  /** True when any edit on this email is processing. */
  hasInFlight: (emailId: string) => boolean
  /** Count of in-flight edits on this email. Drives the toolbar pill. */
  getInFlightCount: (emailId: string) => number
  /**
   * Clear every tracked edit. Used by the workspace shell to drop
   * orphaned emailId-keyed entries when the active brand or org
   * changes — those edits' target emails are no longer visible and
   * any in-flight responses would land in a remounted canvas tree.
   */
  reset: () => void
}

function buildId(): string {
  return newId()
}

/**
 * Returns a stable result object whose method identities never change so
 * downstream effects don't re-fire on every state update.
 *
 * Internally backed by a `useState<Map<string, Array<PendingEdit>>>`.
 * Multiple in-flight edits on the same email coexist in the same list —
 * there is no per-email serial gate. Order is enqueue order.
 */
export function useElementEditTracker(): UseElementEditTrackerResult {
  const [byEmail, setByEmail] = useState<Map<string, Array<PendingEdit>>>(
    () => new Map()
  )

  const updateByEditId = useCallback(
    (
      id: string,
      mutate: (
        edit: PendingEdit,
        list: ReadonlyArray<PendingEdit>
      ) => Array<PendingEdit>
    ) => {
      setByEmail((prev) => {
        for (const [emailId, list] of prev) {
          const idx = list.findIndex((edit) => edit.id === id)
          if (idx === -1) {
            continue
          }
          const updated = mutate(list[idx], list)
          // Reference-equality short-circuit: mutator signaled no-op.
          if (updated === (list as ReadonlyArray<PendingEdit>)) {
            return prev
          }
          const next = new Map(prev)
          if (updated.length === 0) {
            next.delete(emailId)
          } else {
            next.set(emailId, updated)
          }
          return next
        }
        return prev
      })
    },
    []
  )

  const start = useCallback((input: StartElementEditInput): string => {
    const id = buildId()
    const edit: PendingEdit = {
      id,
      emailId: input.emailId,
      prompt: input.prompt,
      nodeType: input.nodeType,
      jsxSnippet: input.jsxSnippet,
      ancestorsPath: input.ancestorsPath,
      pos: input.pos,
      submitTs: Date.now(),
      status: 'processing',
    }
    setByEmail((prev) => {
      const next = new Map(prev)
      const list = next.get(input.emailId) ?? []
      next.set(input.emailId, [...list, edit])
      return next
    })
    return id
  }, [])

  const complete = useCallback(
    (id: string) => {
      updateByEditId(id, (edit, list) => {
        if (edit.status !== 'processing') {
          return list as Array<PendingEdit>
        }
        return list.filter((other) => other.id !== id)
      })
    },
    [updateByEditId]
  )

  const listForEmail = useCallback(
    (emailId: string): Array<PendingEdit> => byEmail.get(emailId) ?? [],
    [byEmail]
  )

  const hasInFlight = useCallback(
    (emailId: string): boolean => {
      const list = byEmail.get(emailId) ?? []
      return list.some((edit) => IN_FLIGHT_STATUSES.has(edit.status))
    },
    [byEmail]
  )

  const getInFlightCount = useCallback(
    (emailId: string): number => {
      const list = byEmail.get(emailId) ?? []
      let count = 0
      for (const edit of list) {
        if (IN_FLIGHT_STATUSES.has(edit.status)) {
          count += 1
        }
      }
      return count
    },
    [byEmail]
  )

  const reset = useCallback(() => {
    setByEmail((prev) => (prev.size === 0 ? prev : new Map()))
  }, [])

  return useMemo(
    () => ({
      start,
      complete,
      listForEmail,
      hasInFlight,
      getInFlightCount,
      reset,
    }),
    [start, complete, listForEmail, hasInFlight, getInFlightCount, reset]
  )
}
